import { Loader2, Map as MapIcon, Plus, UserPlus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { LaunchCampaignSummary } from "@/lib/core/schemas/launch-hub";
import logger from "@/lib/logger";
import { AccountPanel } from "@/src/components/launch/account-panel";
import {
  ArchivedCampaignCard,
  CampaignCard,
} from "@/src/components/launch/campaign-card";
import { CharacterCard } from "@/src/components/launch/character-card";
import { CreateCampaignModal } from "@/src/components/wizard/create-campaign-modal";
import { archiveCampaign } from "../api/campaigns";
import { useEntitlement } from "../hooks/use-entitlement";
import { useLaunchHub } from "../hooks/use-launch-hub";

const ENTITLEMENT_REASON = "You need an active invite to do this.";
const NO_SELECTABLE_CHARACTER_REASON = "Create a character first.";

type LaunchTab = "characters" | "campaigns" | "account";

function LoadingSkeleton() {
  // <output> carries an implicit "status" ARIA role, so no role override is
  // needed (biome's useSemanticElements rule) — a fixed-height skeleton
  // that never shifts layout once the model resolves.
  return (
    <output
      aria-busy="true"
      aria-label="Loading launch hub"
      className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4"
    >
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-10 w-72" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton className="h-40 w-full" key={i} />
        ))}
      </div>
    </output>
  );
}

function FailedState({ retry }: { retry: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center">
      <p className="font-semibold text-lg">Couldn't load your launch hub.</p>
      <p className="max-w-sm text-muted-foreground text-sm">
        Something went wrong reaching your characters and campaigns. Check your
        connection and try again.
      </p>
      <Button onClick={retry} type="button">
        Retry
      </Button>
    </div>
  );
}

export default function LaunchHubPage() {
  const navigate = useNavigate();
  const hub = useLaunchHub();
  const entitlementState = useEntitlement(true);
  const isEntitled = Boolean(entitlementState.entitlement);
  // While entitlement is still resolving, `isEntitled` is false but that's
  // not yet a verified "you're not entitled" claim — suppress the reason
  // text (controls stay disabled either way) until the check settles.
  const entitlementReason = entitlementState.loading
    ? null
    : ENTITLEMENT_REASON;

  const [activeTab, setActiveTab] = useState<LaunchTab>("characters");
  const [preselectedCharacterId, setPreselectedCharacterId] = useState<
    string | null
  >(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] =
    useState<LaunchCampaignSummary | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  if (hub.state === "loading") {
    return <LoadingSkeleton />;
  }
  if (hub.state === "failed") {
    return <FailedState retry={hub.retry} />;
  }

  const { model, refresh } = hub;
  const isRefreshing = hub.state === "refreshing";

  const selectableCharacters = model.characters.filter(
    (c) => c.status.kind === "available" && c.launchSupport.kind === "supported"
  );
  const activeCampaigns = model.campaigns.filter((c) => !c.archivedAt);
  const archivedCampaigns = model.campaigns.filter((c) => c.archivedAt);

  const newCampaignReason = isEntitled
    ? selectableCharacters.length === 0
      ? NO_SELECTABLE_CHARACTER_REASON
      : null
    : entitlementReason;
  const canOpenNewCampaign = isEntitled && selectableCharacters.length > 0;

  const handleLaunchFromCharacter = (characterId: string) => {
    setPreselectedCharacterId(characterId);
    setActiveTab("campaigns");
  };

  const handleOpenArchiveDialog = (campaign: LaunchCampaignSummary) => {
    setArchiveError(null);
    setArchiveTarget(campaign);
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) {
      return;
    }
    setArchiving(true);
    setArchiveError(null);
    try {
      await archiveCampaign(archiveTarget.id);
      setArchiveTarget(null);
      await refresh();
    } catch (err) {
      logger.error({ err }, "[LaunchHub] archive campaign failed");
      setArchiveError("Couldn't archive this campaign. Try again.");
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-4xl flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="font-semibold text-xl">Launch hub</h1>
        {isRefreshing && (
          <span className="flex items-center gap-1 text-muted-foreground text-xs">
            <Loader2 className="h-3 w-3 animate-spin" />
            Refreshing…
          </span>
        )}
      </div>

      <Tabs
        onValueChange={(value) => setActiveTab(value as LaunchTab)}
        value={activeTab}
      >
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="characters">Characters</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="characters">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-muted-foreground text-sm">
                Your characters
              </h2>
              <div className="flex flex-col items-end gap-1">
                <Button
                  disabled={!isEntitled}
                  onClick={() => navigate("/characters/new")}
                  size="sm"
                  type="button"
                >
                  <UserPlus className="h-4 w-4" />
                  Create character
                </Button>
                {!isEntitled && entitlementReason && (
                  <p className="text-muted-foreground text-xs">
                    {entitlementReason}
                  </p>
                )}
              </div>
            </div>
            {model.characters.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
                <p className="font-medium">No characters yet</p>
                <p className="max-w-sm text-muted-foreground text-sm">
                  Create your first character to launch a campaign.
                </p>
                <Button
                  disabled={!isEntitled}
                  onClick={() => navigate("/characters/new")}
                  type="button"
                >
                  <UserPlus className="h-4 w-4" />
                  Create character
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {model.characters.map((character) => (
                  <CharacterCard
                    canLaunch={isEntitled}
                    character={character}
                    key={character.id}
                    launchDisabledReason={isEntitled ? null : entitlementReason}
                    onLaunch={handleLaunchFromCharacter}
                  />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="campaigns">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-muted-foreground text-sm">
                Your campaigns
              </h2>
              <div className="flex flex-col items-end gap-1">
                <Button
                  disabled={!canOpenNewCampaign}
                  onClick={() => setModalOpen(true)}
                  size="sm"
                  type="button"
                >
                  <Plus className="h-4 w-4" />
                  New campaign
                </Button>
                {!canOpenNewCampaign && newCampaignReason && (
                  <p className="text-muted-foreground text-xs">
                    {newCampaignReason}
                  </p>
                )}
              </div>
            </div>

            {activeCampaigns.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
                <MapIcon
                  aria-hidden="true"
                  className="h-8 w-8 text-muted-foreground"
                />
                <p className="font-medium">No active campaigns yet</p>
                <p className="max-w-sm text-muted-foreground text-sm">
                  {selectableCharacters.length === 0
                    ? "Create a character to launch a campaign."
                    : "Launch a campaign from an available character."}
                </p>
                {selectableCharacters.length === 0 ? (
                  <Button
                    disabled={!isEntitled}
                    onClick={() => navigate("/characters/new")}
                    type="button"
                  >
                    <UserPlus className="h-4 w-4" />
                    Create character
                  </Button>
                ) : (
                  <Button
                    disabled={!canOpenNewCampaign}
                    onClick={() => setModalOpen(true)}
                    type="button"
                  >
                    <Plus className="h-4 w-4" />
                    New campaign
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {activeCampaigns.map((campaign) => (
                  <CampaignCard
                    archiveDisabledReason={
                      isEntitled ? null : entitlementReason
                    }
                    campaign={campaign}
                    canArchive={isEntitled}
                    key={campaign.id}
                    onArchive={handleOpenArchiveDialog}
                  />
                ))}
              </div>
            )}

            {archivedCampaigns.length > 0 && (
              <div className="flex flex-col gap-3">
                <h2 className="font-medium text-muted-foreground text-sm">
                  Archived
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {archivedCampaigns.map((campaign) => (
                    <ArchivedCampaignCard
                      campaign={campaign}
                      key={campaign.id}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="account">
          <AccountPanel account={model.account} />
        </TabsContent>
      </Tabs>

      {/*
        preselectedCharacterId is set by the Characters tab's "Launch a
        campaign" affordance (D6). Keying on the preselection here forces a
        clean remount when the user launches from a different character, so
        the modal's preselect effect starts from fresh internal state
        rather than a stale prior selection.
      */}
      <CreateCampaignModal
        characters={model.characters}
        initialCharacterId={preselectedCharacterId ?? undefined}
        key={preselectedCharacterId ?? "unselected"}
        onCreated={(id) => {
          setModalOpen(false);
          setPreselectedCharacterId(null);
          // Character-backed launch: the campaign already has its PC, so
          // the setup wizard runs scenario-only.
          navigate(`/campaigns/${id}/setup?mode=scenario`);
        }}
        onOpenChange={setModalOpen}
        onStale={refresh}
        open={modalOpen}
      />

      <AlertDialog
        onOpenChange={(open) => {
          if (!open) {
            setArchiveTarget(null);
            setArchiveError(null);
          }
        }}
        open={archiveTarget !== null}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Its history and characters are preserved forever; it can't be
              played.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {archiveError && (
            <p className="text-destructive text-sm">{archiveError}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiving}
              onClick={(e) => {
                e.preventDefault();
                handleArchiveConfirm();
              }}
              type="button"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
